import { useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import {
	endOfMonth,
	endOfYear,
	endOfYesterday,
	formatISO,
	startOfDay,
	startOfMonth,
	startOfYear,
	subDays,
	subMonths,
	subYears,
} from "date-fns";
import { useEffect, useState } from "react";
import type { DateRange } from "react-day-picker";

export const QUICK_FILTERS = [
	"all",
	"7d",
	"30d",
	"365d",
	"m",
	"y",
	"-m",
	"-y",
	"custom",
];
export type QuickFilter = (typeof QUICK_FILTERS)[number];

export function buildQuickFiltersData() {
	const maxDay = endOfYesterday();

	return [
		{
			key: "7d",
			value: {
				from: startOfDay(subDays(maxDay, 7 - 1)),
				to: maxDay,
			},
			feature: true,
		},
		{
			key: "30d",
			value: {
				from: subDays(maxDay, 30 - 1),
				to: maxDay,
			},
			feature: true,
		},
		{
			key: "365d",
			value: {
				from: subDays(maxDay, 365 - 1),
				to: maxDay,
			},
			feature: true,
		},
		{
			key: "m",
			value: {
				from: startOfMonth(maxDay),
				to: maxDay,
			},
			feature: false,
		},
		{
			key: "y",
			value: {
				from: startOfYear(maxDay),
				to: maxDay,
			},
			feature: false,
		},
		{
			key: "-m",
			value: {
				from: startOfMonth(subMonths(maxDay, 1)),
				to: endOfMonth(subMonths(maxDay, 1)),
			},
			feature: false,
		},
		{
			key: "-y",
			value: {
				from: startOfYear(subYears(maxDay, 1)),
				to: endOfYear(subYears(maxDay, 1)),
			},
			feature: false,
		},
		{
			key: "all",
			value: {
				from: startOfYear("2020-01-01"),
				to: maxDay,
			},
			feature: true,
		},
		{
			key: "custom",
			feature: false,
		},
	];
}

export function buildQuickFilters() {
	const { t } = useLingui();
	const filters = buildQuickFiltersData();

	const labelMap: Record<QuickFilter, string> = {
		"7d": t`Last 7 Days`,
		"30d": t`Last 30 Days`,
		"365d": t`Last 365 Days`,
		m: t`This Month`,
		y: t`This Year`,
		"-m": t`Last Month`,
		"-y": t`Last Year`,
		all: t`All`,
		custom: t`Custom`,
	};

	return filters.map((filter) => ({
		...filter,
		label: labelMap[filter.key],
	}));
}

export function useChartFilters({
	initialFilter = "7d",
	initialDate,
	syncURL = true,
}: {
	initialFilter?: string;
	initialDate?: DateRange;
	syncURL?: boolean;
}) {
	const navigate = useNavigate();

	const [quickFilter, setQuickFilter] = useState(initialFilter);
	const [customDate, setCustomDate] = useState<DateRange | undefined>(
		initialDate,
	);

	const quickFilters = buildQuickFilters();

	useEffect(() => {
		if (quickFilter === "custom") return;

		const selected = quickFilters.find((f) => f.key === quickFilter);
		if (selected) {
			setCustomDate(selected.value);
		}
	}, [quickFilter]);

	// Update URL when either quickFilter or customDate changes
	useEffect(() => {
		if (!customDate?.from || !customDate?.to) return;
		if (!syncURL) return;

		navigate({
			to: ".",
			search: {
				q: quickFilter,
				from: formatISO(customDate.from, { representation: "date" }),
				to: formatISO(customDate.to, { representation: "date" }),
			},
		});
	}, [quickFilter, customDate]);

	const handleQuickFilterChange = (key: string) => {
		setQuickFilter(key);
		const newQuickFilter = quickFilters.find((f) => f.key === key);
		if (newQuickFilter) {
			setCustomDate(newQuickFilter?.value);
		}
	};

	const handleCustomDateChange = (range: DateRange | undefined) => {
		setCustomDate(range);
		setQuickFilter("custom");
	};

	const filters = {
		q: quickFilter,
		from: customDate?.from
			? formatISO(customDate.from, { representation: "date" })
			: "",
		to: customDate?.to
			? formatISO(customDate.to, { representation: "date" })
			: "",
	};

	return {
		customDate,
		filters,
		quickFilter,
		quickFilters,
		handleQuickFilterChange,
		handleCustomDateChange,
	};
}
